// src/redux/authSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { logout } from '../services/api';
import { AppDispatch, RootState } from './index';
import { ThunkAction } from 'redux-thunk';

interface UserState {
  user: any;
}

// // Check if user data exists in localStorage and initialize state
// const storedUser = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') as string) : null;

const initialState: UserState = {
  user: null,
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<any>) => {
      // Sanitize user data to only store serializable properties
      const sanitizedUser = {
        id: action.payload?.id,
        email: action.payload?.email,
        role: action.payload?.role,
        // Exclude functions, dates, complex objects, and circular references
      };

      state.user = sanitizedUser;
      // Persist user to localStorage
      // Persist sanitized user to localStorage
      localStorage.setItem('user', JSON.stringify(sanitizedUser));
    },
    logoutUser: (state) => {
    // CRITICAL SECURITY: Clear ALL authentication data
      state.user = null;
      // Remove ALL authentication data from localStorage
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');
      // Also clear any other auth-related data
      localStorage.removeItem('user_preferences'); // Clear preferences too
      console.log('🔐 CRITICAL: All authentication data cleared from localStorage');
    },

    initializeUserFromLocalStorage: (state) => {
      try {
        const storedUserString = localStorage.getItem('user');
        if (storedUserString && storedUserString !== 'undefined' && storedUserString !== 'null') {
          const storedUser = JSON.parse(storedUserString);
          if (storedUser && typeof storedUser === 'object') {
            state.user = storedUser;
          }
        }
      } catch (error) {
        console.warn('Failed to parse stored user data, clearing localStorage:', error);
        localStorage.removeItem('user'); // Clear corrupted data
      }
    },

  },
});

// Thunk action to validate and restore authentication from localStorage
export const validateAndRestoreAuth = (): ThunkAction<void, RootState, any, PayloadAction<void>> => async (
  dispatch: AppDispatch
) => {
  console.log('🔄 Starting authentication restoration from localStorage...');

  try {
    const token = localStorage.getItem('authToken');
    const storedUserString = localStorage.getItem('user');

    console.log('📦 Found stored data:', {
      hasToken: !!token,
      hasUserString: !!storedUserString,
      tokenLength: token?.length
    });

    // If no token or user data, nothing to restore
    if (!token || !storedUserString) {
      console.log('❌ No stored authentication data found');
      return;
    }

    // Import getCurrentUser dynamically to avoid circular imports
    console.log('🔄 Importing API service...');
    const { getCurrentUser } = await import('../services/api');

    try {
      console.log('🌐 Validating token with backend...');
      // Validate token by making an authenticated request to /auth/me
      const userData = await getCurrentUser();

      console.log('📨 Received user data:', userData);

      if (userData && userData.email) {
        // Token is valid, restore user state from localStorage
        const storedUserData = JSON.parse(storedUserString);
        console.log('✅ Token valid, restoring user state:', storedUserData);
        if (storedUserData && typeof storedUserData === 'object') {
          dispatch(setUser(storedUserData));
          console.log('✅ Authentication successfully restored from localStorage');
        }
      } else {
        throw new Error('Invalid user data received from API');
      }
    } catch (error: unknown) {
      const err = error as { response?: { status?: number }; code?: string; message?: string };
      const status = err.response?.status;
      const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';
      const isInvalidUserPayload =
        error instanceof Error && error.message === 'Invalid user data received from API';
      const isNetworkFailure =
        (err.response == null && !isInvalidUserPayload) ||
        err.code === 'ECONNABORTED' ||
        err.code === 'ERR_NETWORK' ||
        msg.includes('network error');

      if (status === 401) {
        console.log('❌ Token invalid or expired (401)');
        dispatch(logoutUser());
      } else if (isInvalidUserPayload) {
        console.log('❌ /auth/me returned unusable user payload');
        dispatch(logoutUser());
      } else if (isNetworkFailure) {
        console.warn('⚠️ Auth validation could not reach server (network); keeping existing session');
      } else {
        console.warn(
          '⚠️ Auth validation failed with non-auth error; keeping session to avoid false logouts:',
          status ?? 'no-status',
          err.message
        );
      }
    }
  } catch (error) {
    console.warn('❌ Error restoring authentication:', error);
    // Clear potentially corrupted data
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    dispatch(logoutUser());
  }
};

// Define the performLogout thunk action type
export const performLogout = (): ThunkAction<void, RootState, any, PayloadAction<void>> => async (
  dispatch: AppDispatch
) => {
  try {
    await logout(); // Call the API to log out
    dispatch(logoutUser()); // Dispatch the logout action
  } catch (error) {
    console.error('Logout failed:', error);
  }
};

export const { setUser, logoutUser, initializeUserFromLocalStorage } = authSlice.actions;
export default authSlice.reducer;
